import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateWashingReportCache, useCreateWashingReport, useUpdateWashingReport } from 'src/api/washing-report'
import BasicDatePicker from 'src/components/DateTimePicker'
import { FoulingAmount, FoulingType, WashingReportDto } from 'src/generated-types/washing-report-dto'
import { z } from 'zod'
import { AppForm, FormCheckbox, FormFileCoreUpload, FormSelectField, FormTextField } from '../../../components/FormCore'
import { GenericDialogLayout } from '../../../components/GenericDialogLayout'
import { MainButton } from '../../../components/MainButton'
import { useZodForm } from '../../../hooks/zod-form'
import { ShowDialogProps } from '../../../show-dialog'
import { ReactComponent as FileIcon } from 'src/assets/svg/fileUploadIcon.svg'
import { invalidateMainComponentCache } from 'src/api/main-component'
import { invalidateLocalityCache } from 'src/api/localities'
import { useParams } from 'react-router'

interface WashingReportFormDialogProps extends ShowDialogProps {
  customerId: EntityId
  mainComponentId: EntityId
  initialData?: WashingReportDto
  positionId?: EntityId
}

export const WashingReportFormDialog: React.FC<WashingReportFormDialogProps> = ({
  pop,
  customerId,
  mainComponentId,
  initialData,
  positionId,
}) => {
  const { t } = useTranslation(['equipment', 'common'])
  const { localityId } = useParams()
  const isEdit = !!initialData?.id
  const { mutateAsync: addMutation, isLoading: isCreating } = useCreateWashingReport(mainComponentId)
  const { mutateAsync: updateMutation, isLoading: isUpdating } = useUpdateWashingReport(
    mainComponentId,
    initialData?.id as EntityId,
  )

  const schema = z.object({
    foulingType: z.preprocess(
      val => (val === '' || val === null || val === undefined || Number.isNaN(Number(val)) ? -1 : Number(val)),
      z.number().min(0, t('required', { ns: 'common' })),
    ),
    foulingAmount: z.preprocess(
      val => (val === '' || val === null || val === undefined || Number.isNaN(Number(val)) ? -1 : Number(val)),
      z.number().min(0, t('required', { ns: 'common' })),
    ),
    pressure: z.preprocess(
      val => Number(val),
      z
        .number()
        .int()
        .nonnegative(t('provideValidInput', { ns: 'common' }))
        .min(1, t('required', { ns: 'common' })),
    ),
    timeConsumption: z.preprocess(
      val => Number(val),
      z
        .number()
        .nonnegative(t('provideValidInput', { ns: 'common' }))
        .min(1, t('required', { ns: 'common' })),
    ),
    notes: z.string().optional(),
    inspection: z.boolean().optional(),
    extraTimeConsumption: z.boolean().optional(),
    washedOn: z.preprocess(val => {
      if (typeof val === 'string') {
        return new Date(val)
      }
      return val
    }, z.date()),
    fileIds: z.array(z.number()).optional(),
  })

  const form = useZodForm(schema, {
    defaultValues: initialData
      ? {
        ...initialData,
        fileIds: initialData?.washingReportAttachments?.map(att => att.file.id),
        washedOn: initialData?.washedOn?.startsWith('0001-01-01') ? null : new Date(initialData?.washedOn),
      }
      : {
        foulingType: '',
        foulingAmount: '',
        pressure: 0,
        timeConsumption: 0,
        notes: '',
        inspection: false,
        extraTimeConsumption: false,
        washedOn: new Date(),
        fileIds: [],
      },
  })

  const onSubmit = async (formData: any) => {
    if (isEdit) {
      await updateMutation(
        {
          id: initialData?.id,
          mainComponentId,
          ...formData,
          pressure: formData.pressure as number,
          timeConsumption: formData.timeConsumption as number,
        },
        {
          onSuccess: () => {
            toast.success(t('updatedSuccessfully', { ns: 'common' }))
            invalidateMainComponentCache.getEquipment(customerId as EntityId)
            invalidateWashingReportCache.getWashingReports(mainComponentId)
            invalidationActivityLogCache.getCageActivityLogs()
            invalidateLocalityCache.getCageStatus(customerId as EntityId, localityId as EntityId)
            invalidateMainComponentCache.getMaincomponentsBySearchWithFilters(customerId)
          },
        },
      )
    } else {
      await addMutation(
        {
          mainComponentId,
          ...formData,
          positionId,
          pressure: formData.pressure as number,
          timeConsumption: formData.timeConsumption as number,
        },
        {
          onSuccess: () => {
            toast.success(t('addedSuccessfully', { ns: 'common' }))
            invalidateMainComponentCache.getEquipment(customerId as EntityId)
            invalidateWashingReportCache.getWashingReports(mainComponentId)
            invalidationActivityLogCache.getCageActivityLogs()
            invalidateLocalityCache.getCageStatus(customerId as EntityId, localityId as EntityId)
            invalidateMainComponentCache.getMaincomponentsBySearchWithFilters(customerId)
          },
        },
      )
    }
    pop()
  }

  return (
    <GenericDialogLayout
      title={isEdit ? t('edit-washing-report', { ns: 'equipment' }) : t('add-washing-report', { ns: 'equipment' })}
      pop={pop}
      actions={[
        <MainButton
          disabled={isCreating || isUpdating}
          onClick={() => form.handleSubmit(onSubmit)()}
          key={'add-update-btn'}
        >
          {isCreating || isUpdating
            ? t('saving', { ns: 'common' })
            : isEdit
              ? t('update', { ns: 'common' })
              : t('add', { ns: 'common' })}
        </MainButton>,
      ]}
    >
      <AppForm form={form}>
        <FormSelectField
          name={'foulingType'}
          label={t('fouling-type', { ns: 'equipment' })}
          data={Object.keys(FoulingType).filter(key => !isNaN(Number(key)))}
          getOptionLabel={option => {
            const label = FoulingType[option as keyof typeof FoulingType]
            return label !== undefined ? t(label.toString(), { ns: 'equipment' }) : ''
          }}
          isOptionEqualToValue={(o, v) => o.toString() === v.toString()}
          onChange={(e, value) => {
            form.setValue('foulingType', (value === null || value === undefined ? '' : parseInt(value as any)) as any)
          }}
          renderOption={(props: any, option) => {
            return (
              <Box {...props} key={option}>
                {t(FoulingType[option as keyof typeof FoulingType].toString(), { ns: 'equipment' })}
              </Box>
            )
          }}
        />
        <FormSelectField
          name={'foulingAmount'}
          label={t('fouling-amount', { ns: 'equipment' })}
          data={Object.keys(FoulingAmount).filter(key => !isNaN(Number(key)))}
          getOptionLabel={option => {
            const label = FoulingAmount[option as keyof typeof FoulingAmount]
            return label !== undefined ? t(label.toString(), { ns: 'equipment' }) : ''
          }}
          isOptionEqualToValue={(o, v) => o.toString() === v.toString()}
          onChange={(e, value) => {
            form.setValue('foulingAmount', (value === null || value === undefined ? '' : parseInt(value as any)) as any)
          }}
          renderOption={(props: any, option) => {
            return (
              <Box {...props} key={option}>
                {t(FoulingAmount[option as keyof typeof FoulingAmount].toString(), { ns: 'equipment' })}
              </Box>
            )
          }}
        />
        <FormTextField name="pressure" label={t('pressure', { ns: 'equipment' })} type="number" fullWidth />
        <FormTextField
          name="timeConsumption"
          label={t('time-consumption', { ns: 'equipment' })}
          type="number"
          fullWidth
        />
        <BasicDatePicker name="washedOn" label={t('washed-on', { ns: 'equipment' })} />
        <FormTextField name="notes" label={t('notes', { ns: 'equipment' })} multiline rows={3} fullWidth />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormCheckbox name="inspection" label={t('inspection', { ns: 'equipment' }) as string} />
          <FormCheckbox
            name="extraTimeConsumption"
            label={t('extra-time-consumption', { ns: 'equipment' }) as string}
          />
        </Box>
        <FormFileCoreUpload
          label={t('fileName', { ns: 'common' })}
          name="fileIds"
          mapFileKey="id"
          multiple
          FileIcon={FileIcon}
          height="175px"
          initialFiles={initialData?.washingReportAttachments?.map(att => att.file)}
        />
      </AppForm>
    </GenericDialogLayout>
  )
}
